/**
 * StaffDetailPage — `/admin/staff/:id` internal-employee profile.
 *
 * Data source (SCRUM-131): there is NO `GET /admin/staff/:id` endpoint.
 * The detail is sourced from `useStaffList()` (`GET /admin/staff`) then
 * found by id. Explicit result contract:
 *
 *   - query `loading`            → loading panel;
 *   - `error`                    → error panel;
 *   - `forbidden`                → not-permitted panel;
 *   - `ready` + id NOT in members → not-found panel;
 *   - `ready` + hit              → the detail page.
 *
 * Layout (artboard `admin.jsx` StaffDetailPage):
 *   - Breadcrumb "‹ Staff".
 *   - Hero card (brand-tinted radial gradient): Avatar + StaffRoleChip +
 *     status badge + name + mono email, with Reset password / Suspend /
 *     Reactivate actions; a 3-stat band beneath.
 *   - Two-column body: Identity card + Access card (role-derived
 *     permission list).
 *
 * Fields with no backend source (scope / last active / last IP / 2FA)
 * render an honest "—" placeholder (decision-4) rather than fabricated
 * data. `name` arrives already normalized to a non-empty string from
 * the `useStaffList` mapper.
 *
 * Gating (SCRUM-131, codex BLOCKING-1):
 *   - Suspend / Reactivate → `useSuspendStaff` / `useReactivateStaff`,
 *     gated `staff:suspend` (root only) + hidden for self.
 *   - Reset password → `useResetStaffPassword`, gated
 *     `staff:reset_password` (root AND support — `PlatformOnlyGuard`).
 *
 * The Danger-zone "Remove from platform" is intentionally REMOVED: no
 * `DELETE /admin/staff/:id` endpoint exists; suspend is the lifecycle
 * action. reset-2fa / sign-out-all are deferred to a BE follow-up.
 */

import * as React from 'react';
import { Link, useParams } from 'react-router-dom';
import { Avatar, Button, Card, Icon } from '@sparta/ui-kit';
import { IfAdminPermission } from '../../../shared/auth/IfAdminPermission.js';
import {
  PERMISSIONS_BY_ROLE,
  type AdminPermissionSlug,
} from '../../../shared/auth/admin-permissions.js';
import { useMe } from '../../../shared/auth/use-me.js';
import { StaffRoleChip } from '../_components/StaffRoleChip.js';
import { useCompactMode } from '../../../shared/hooks/useCompactMode.js';
import {
  useStaffList,
  useSuspendStaff,
  useReactivateStaff,
  useResetStaffPassword,
  useResetStaff2fa,
  useSignOutAllStaffSessions,
  type StaffMember,
  type ResetStaffPasswordResult,
  type ResetStaff2faResult,
  type SignOutAllStaffSessionsResult,
} from '../api/index.js';
import { StaffStatusBadge, type StaffRow, type StaffStatus } from './StaffListPage.js';

// ── Honest placeholder ─────────────────────────────────────────

/** Em-dash for any field with no backend source (decision-4). */
const NO_BACKEND_SOURCE = '—';

// ── Page ───────────────────────────────────────────────────────

export interface StaffDetailPageProps {
  readonly compact?: boolean;
}

export function StaffDetailPage(
  props: StaffDetailPageProps = {},
): React.ReactElement {
  return (
    <IfAdminPermission permission="staff:read" fallback={<NotPermittedPanel />}>
      <StaffDetailBody {...props} />
    </IfAdminPermission>
  );
}

// ── Panels ─────────────────────────────────────────────────────

function CenteredPanel({
  headingId,
  heading,
  children,
  busy,
}: {
  readonly headingId?: string;
  readonly heading?: string;
  readonly children: React.ReactNode;
  readonly busy?: boolean;
}): React.ReactElement {
  return (
    <section
      aria-labelledby={headingId}
      aria-busy={busy ? 'true' : undefined}
      aria-live={busy ? 'polite' : undefined}
      className="mx-auto flex max-w-md flex-col items-center justify-center px-4 py-16 text-center"
    >
      {heading !== undefined ? (
        <h1
          id={headingId}
          className="text-xl font-semibold"
          style={{ color: 'var(--fg-primary)' }}
        >
          {heading}
        </h1>
      ) : null}
      {children}
    </section>
  );
}

function BackToStaff(): React.ReactElement {
  return (
    <div style={{ marginTop: '16px' }}>
      <Button asChild variant="secondary">
        <Link to="/admin/staff">Back to Staff</Link>
      </Button>
    </div>
  );
}

function NotPermittedPanel(): React.ReactElement {
  return (
    <CenteredPanel headingId="staff-detail-not-permitted-heading" heading="Not permitted">
      <p className="mt-2 text-sm" style={{ color: 'var(--fg-secondary)' }}>
        You don't have access to view staff details. Ask a platform
        administrator if you need this access.
      </p>
      <BackToStaff />
    </CenteredPanel>
  );
}

function NotFoundPanel({ id }: { readonly id: string | undefined }): React.ReactElement {
  return (
    <CenteredPanel headingId="staff-detail-not-found-heading" heading="Staff member not found">
      <p className="mt-2 text-sm" style={{ color: 'var(--fg-secondary)' }}>
        {id === undefined
          ? 'No staff id was provided.'
          : `No staff member found for id "${id}".`}
      </p>
      <BackToStaff />
    </CenteredPanel>
  );
}

function LoadingPanel(): React.ReactElement {
  return (
    <CenteredPanel busy>
      <p className="text-sm" style={{ color: 'var(--fg-secondary)' }}>
        Loading staff detail…
      </p>
    </CenteredPanel>
  );
}

function ErrorPanel({ onRetry }: { readonly onRetry: () => void }): React.ReactElement {
  return (
    <CenteredPanel headingId="staff-detail-error-heading" heading="Couldn't load staff">
      <p className="mt-2 text-sm" style={{ color: 'var(--fg-secondary)' }}>
        Something went wrong loading staff details. Try again.
      </p>
      <div style={{ marginTop: '16px' }}>
        <Button type="button" variant="secondary" onClick={onRetry}>
          Retry
        </Button>
      </div>
    </CenteredPanel>
  );
}

// ── Body ───────────────────────────────────────────────────────

function StaffDetailBody(props: StaffDetailPageProps): React.ReactElement {
  const { id } = useParams<{ id: string }>();
  const compact = useCompactMode(props.compact);
  const list = useStaffList();
  const me = useMe();
  const meId = me?.user.id;

  const suspend = useSuspendStaff();
  const reactivate = useReactivateStaff();
  const resetPassword = useResetStaffPassword();
  const reset2fa = useResetStaff2fa();
  const signOutAll = useSignOutAllStaffSessions();

  // WCAG 4.1.3 Status Messages: outcomes of the async action mutations
  // (suspend / reactivate / reset-password) are announced to assistive
  // tech without moving focus. `success` → polite status region;
  // `error` → assertive alert region. Cleared before each new action.
  const [announcement, setAnnouncement] = React.useState<
    { readonly kind: 'success' | 'error'; readonly message: string } | null
  >(null);

  if (list.status === 'loading') return <LoadingPanel />;
  if (list.status === 'forbidden') return <NotPermittedPanel />;
  if (list.status === 'error') return <ErrorPanel onRetry={list.refetch} />;

  const staff: StaffMember | undefined = list.members.find((m) => m.id === id);
  if (staff === undefined) return <NotFoundPanel id={id} />;

  const isSelf = meId !== undefined && meId === staff.id;

  const ACTION_FAILED = "Couldn't complete that action. Try again.";
  // Non-leaky copy shared by the recovery actions (FE-2). No internal
  // identifiers (slugs / status codes / ports) — the user can't act on
  // those and they leak debug context.
  const NO_PERMISSION = "You don't have permission to perform this action.";
  const CANNOT_SELF = "You can't perform this action on your own account.";
  const TOO_MANY = 'Too many attempts. Try again later.';

  const handleSuspendToggle = (): void => {
    setAnnouncement(null);
    const willSuspend = staff.status === 'active';
    const run = willSuspend
      ? suspend.mutateAsync(staff.id)
      : reactivate.mutateAsync(staff.id);
    void run
      .then(() => {
        setAnnouncement({
          kind: 'success',
          message: `${staff.name} ${willSuspend ? 'suspended' : 'reactivated'}`,
        });
      })
      .catch(() => {
        setAnnouncement({ kind: 'error', message: ACTION_FAILED });
      });
  };

  const handleResetPassword = (): void => {
    setAnnouncement(null);
    void resetPassword
      .mutateAsync(staff.id)
      .then((result: ResetStaffPasswordResult) => {
        switch (result.kind) {
          case 'success':
            setAnnouncement({
              kind: 'success',
              message: `Password-reset email sent to ${staff.email}`,
            });
            break;
          case 'forbidden':
            // support→root guardrail (retrofit): surface a clear permission
            // message rather than the opaque generic error.
            setAnnouncement({ kind: 'error', message: NO_PERMISSION });
            break;
          case 'rateLimited':
            setAnnouncement({ kind: 'error', message: TOO_MANY });
            break;
          default:
            setAnnouncement({ kind: 'error', message: ACTION_FAILED });
        }
      })
      .catch(() => {
        setAnnouncement({ kind: 'error', message: ACTION_FAILED });
      });
  };

  const handleReset2fa = (): void => {
    setAnnouncement(null);
    void reset2fa
      .mutateAsync(staff.id)
      .then((result: ResetStaff2faResult) => {
        switch (result.kind) {
          case 'success':
            setAnnouncement({
              kind: 'success',
              message: `Two-factor authentication was reset for ${staff.name}. They'll be prompted to re-enroll.`,
            });
            break;
          case 'forbidden':
            setAnnouncement({ kind: 'error', message: NO_PERMISSION });
            break;
          case 'cannotSelf':
            setAnnouncement({ kind: 'error', message: CANNOT_SELF });
            break;
          case 'rateLimited':
            setAnnouncement({ kind: 'error', message: TOO_MANY });
            break;
          default:
            setAnnouncement({ kind: 'error', message: ACTION_FAILED });
        }
      })
      .catch(() => {
        setAnnouncement({ kind: 'error', message: ACTION_FAILED });
      });
  };

  const handleSignOutAll = (): void => {
    setAnnouncement(null);
    void signOutAll
      .mutateAsync(staff.id)
      .then((result: SignOutAllStaffSessionsResult) => {
        switch (result.kind) {
          case 'success':
            setAnnouncement({
              kind: 'success',
              message: `Signed out ${result.revokedCount} session${result.revokedCount === 1 ? '' : 's'} for ${staff.name}.`,
            });
            break;
          case 'forbidden':
            setAnnouncement({ kind: 'error', message: NO_PERMISSION });
            break;
          case 'cannotSelf':
            setAnnouncement({ kind: 'error', message: CANNOT_SELF });
            break;
          default:
            setAnnouncement({ kind: 'error', message: ACTION_FAILED });
        }
      })
      .catch(() => {
        setAnnouncement({ kind: 'error', message: ACTION_FAILED });
      });
  };

  const accessSentence =
    'This employee has platform-wide access bounded by their role.';

  // Shared action affordances. Rendered inline (shrink-0 row) in the
  // hero on desktop, and as a stacked full-width section below the hero
  // identity block in compact mode (codex P1) — same handlers, same
  // gating, ≥44 px touch targets via the `full` button variant.
  const resetPasswordButton = (
    <IfAdminPermission permission="staff:reset_password">
      <Button
        type="button"
        variant="secondary"
        size={compact ? 'lg' : 'md'}
        full={compact}
        disabled={resetPassword.isPending}
        onClick={handleResetPassword}
      >
        <Icon name="key" size={16} aria-hidden />
        Reset password
      </Button>
    </IfAdminPermission>
  );
  const suspendButton =
    !isSelf ? (
      <IfAdminPermission permission="staff:suspend">
        <Button
          type="button"
          variant="secondary"
          size={compact ? 'lg' : 'md'}
          full={compact}
          disabled={suspend.isPending || reactivate.isPending}
          onClick={handleSuspendToggle}
        >
          <Icon name="lock" size={16} aria-hidden />
          {staff.status === 'active' ? 'Suspend' : 'Reactivate'}
        </Button>
      </IfAdminPermission>
    ) : null;
  // Recovery actions (FE-2). Both root+support (PlatformOnlyGuard) and
  // both hidden for self — resetting your own 2FA / killing your own
  // sessions would lock you out; the server also rejects (defense in
  // depth). Self-service is out of scope.
  const reset2faButton =
    !isSelf ? (
      <IfAdminPermission permission="staff:reset_2fa">
        <Button
          type="button"
          variant="secondary"
          size={compact ? 'lg' : 'md'}
          full={compact}
          disabled={reset2fa.isPending}
          onClick={handleReset2fa}
        >
          <Icon name="shield" size={16} aria-hidden />
          Reset 2FA
        </Button>
      </IfAdminPermission>
    ) : null;
  const signOutAllButton =
    !isSelf ? (
      <IfAdminPermission permission="staff:sign_out_all">
        <Button
          type="button"
          variant="secondary"
          size={compact ? 'lg' : 'md'}
          full={compact}
          disabled={signOutAll.isPending}
          onClick={handleSignOutAll}
        >
          <Icon name="logout" size={16} aria-hidden />
          Sign out all sessions
        </Button>
      </IfAdminPermission>
    ) : null;

  return (
    <article
      aria-labelledby="staff-detail-heading"
      className="mx-auto w-full"
      style={{ maxWidth: '1100px', padding: compact ? '20px 16px 80px' : '28px 48px 80px' }}
    >
      <Link
        to="/admin/staff"
        className="inline-flex items-center no-underline"
        style={{ gap: '6px', fontSize: '13px', color: 'var(--fg-tertiary)', marginBottom: '14px' }}
      >
        <Icon name="chevLeft" size={13} aria-hidden /> Staff
      </Link>

      {/* Action announcement (WCAG 4.1.3 Status Messages). AT registers
          live regions at MOUNT time — a node injected already containing
          text is not announced. So BOTH regions are PERSISTENT (always
          in the DOM, even when empty); the message text is routed into
          the matching one by kind. The polite `status` region carries
          successes; the assertive `alert` region carries errors. Visible
          colored styling lives on the inner message element so the empty
          containers stay invisible to sighted users. */}
      <ActionAnnouncer announcement={announcement} />


      {/* Hero */}
      <Card padding="none">
        <div className="relative overflow-hidden" style={{ borderRadius: 'var(--radius-2xl)' }}>
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                'radial-gradient(ellipse 700px 280px at 92% -10%, color-mix(in oklab, var(--brand-500) 14%, transparent), transparent 70%)',
            }}
          />
          <div
            className="relative flex flex-wrap items-start"
            style={{ gap: '20px', padding: compact ? '20px 20px 0' : '28px 32px 0' }}
          >
            <Avatar name={staff.name} size={72} />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center" style={{ gap: '8px', marginBottom: '6px' }}>
                <StaffRoleChip role={staff.role} />
                <StaffStatusBadge status={staff.status} />
              </div>
              <h1
                id="staff-detail-heading"
                className="m-0 font-semibold"
                style={{ fontSize: compact ? '22px' : '28px', letterSpacing: '-0.4px', color: 'var(--fg-primary)' }}
              >
                {staff.name}
              </h1>
              <div
                className="font-mono"
                style={{ marginTop: '6px', fontSize: '13px', color: 'var(--fg-secondary)' }}
              >
                {staff.email}
              </div>
            </div>
            {/* Desktop: inline shrink-0 action cluster in the hero. On
                compact this moves to a dedicated stacked row below the
                hero (see below) so the actions never vanish on phones. */}
            {!compact ? (
              <div className="flex shrink-0 flex-wrap" style={{ gap: '8px' }}>
                {/* Reset password is PlatformOnlyGuard → root AND support. */}
                {resetPasswordButton}
                {/* Reset 2FA / Sign out all — root+support, hidden for self. */}
                {reset2faButton}
                {signOutAllButton}
                {/* Suspend / Reactivate is root-only + hidden for self. */}
                {suspendButton}
              </div>
            ) : null}
          </div>

          {/* Compact: stacked, full-width action row below the hero
              identity block (codex P1). Same affordances + gating as
              desktop; reachable on phones/tablets. */}
          {compact ? (
            <div
              className="relative flex flex-col items-stretch"
              style={{ gap: '8px', padding: '16px 20px 0' }}
            >
              {resetPasswordButton}
              {reset2faButton}
              {signOutAllButton}
              {suspendButton}
            </div>
          ) : null}
          {/* Stat band — every stat here has no backend source today. */}
          <div
            className="relative grid"
            style={{
              marginTop: '24px',
              gridTemplateColumns: compact ? '1fr 1fr' : '1fr 1fr 1fr',
              borderTop: '1px solid var(--border-subtle)',
            }}
          >
            <StatCell label="Scope" value={NO_BACKEND_SOURCE} />
            <StatCell label="Last active" value={NO_BACKEND_SOURCE} bordered />
            {!compact ? <StatCell label="Last IP" value={NO_BACKEND_SOURCE} bordered /> : null}
          </div>
        </div>
      </Card>

      {/* Body */}
      <div
        className="mt-4 grid"
        style={{
          gap: '16px',
          marginTop: '16px',
          gridTemplateColumns: compact ? '1fr' : '1fr 1fr',
        }}
      >
        <Card padding="lg">
          <h2 className="m-0 font-bold" style={{ fontSize: '16px', color: 'var(--fg-primary)', marginBottom: '16px' }}>
            Identity
          </h2>
          <DetailRow label="Email" value={staff.email} mono />
          <DetailRow label="Display name" value={staff.name} />
          <DetailRow label="Role" value={<StaffRoleChip role={staff.role} />} />
          <DetailRow label="Scope" value={NO_BACKEND_SOURCE} />
          <DetailRow label="2FA" value={NO_BACKEND_SOURCE} />
        </Card>
        <Card padding="lg">
          <h2 className="m-0 font-bold" style={{ fontSize: '16px', color: 'var(--fg-primary)', marginBottom: '4px' }}>
            Access
          </h2>
          <p className="m-0" style={{ fontSize: '13px', color: 'var(--fg-tertiary)', marginBottom: '16px' }}>
            {accessSentence}
          </p>
          <ul className="m-0 flex list-none flex-col p-0" style={{ gap: '6px' }} data-testid="staff-detail-permissions">
            {(PERMISSIONS_BY_ROLE[staff.role] ?? []).map((slug: AdminPermissionSlug) => (
              <li
                key={slug}
                className="flex items-center"
                style={{ gap: '8px', padding: '8px 10px', background: 'var(--bg-sunken)', borderRadius: 'var(--radius-md)', fontSize: '13px' }}
              >
                <span aria-hidden="true" style={{ color: 'var(--success-500)' }}>
                  <Icon name="check" size={14} />
                </span>
                <code className="font-mono">{slug}</code>
              </li>
            ))}
          </ul>
        </Card>
      </div>
    </article>
  );
}

// ── Action announcer (WCAG 4.1.3, pre-mounted live regions) ────

/** Discriminated announcement payload shared by the action handlers. */
type Announcement = {
  readonly kind: 'success' | 'error';
  readonly message: string;
} | null;

/**
 * Two always-present sibling live regions. AT registers live regions at
 * mount; injecting a node that already contains text is NOT announced.
 * Pre-mounting both an assertive `alert` and a polite `status` region and
 * routing the message into the matching one by kind keeps the politeness
 * distinction while guaranteeing the announcement fires. The visible
 * colored text lives on the inner element, so the empty container is
 * invisible until a message arrives.
 */
function ActionAnnouncer({
  announcement,
}: {
  readonly announcement: Announcement;
}): React.ReactElement {
  const isError = announcement?.kind === 'error';
  const isSuccess = announcement?.kind === 'success';
  return (
    <>
      <div role="status" aria-live="polite" aria-label="Action result">
        {isSuccess ? (
          <p
            className="m-0"
            style={{
              marginBottom: '14px',
              fontSize: '13px',
              color: 'var(--fg-secondary)',
            }}
          >
            {announcement?.message}
          </p>
        ) : null}
      </div>
      <div role="alert" aria-live="assertive" aria-label="Action error">
        {isError ? (
          <p
            className="m-0"
            style={{
              marginBottom: '14px',
              fontSize: '13px',
              color: 'var(--fg-danger)',
            }}
          >
            {announcement?.message}
          </p>
        ) : null}
      </div>
    </>
  );
}

// ── Small presentational helpers ───────────────────────────────

function StatCell({
  label,
  value,
  bordered,
}: {
  readonly label: string;
  readonly value: string;
  readonly bordered?: boolean;
}): React.ReactElement {
  return (
    <div
      style={{
        padding: '16px 20px',
        borderLeft: bordered ? '1px solid var(--border-subtle)' : undefined,
      }}
    >
      <div
        className="font-bold uppercase"
        style={{ fontSize: '11px', letterSpacing: '0.6px', color: 'var(--fg-tertiary)', marginBottom: '4px' }}
      >
        {label}
      </div>
      <div className="font-semibold" style={{ fontSize: '18px', color: 'var(--fg-primary)' }}>
        {value}
      </div>
    </div>
  );
}

function DetailRow({
  label,
  value,
  mono,
}: {
  readonly label: string;
  readonly value: React.ReactNode;
  readonly mono?: boolean;
}): React.ReactElement {
  return (
    <div
      className="flex items-center justify-between"
      style={{ gap: '16px', padding: '10px 0', borderBottom: '1px solid var(--border-subtle)' }}
    >
      <span
        className="font-bold uppercase shrink-0"
        style={{ fontSize: '11px', letterSpacing: '0.6px', color: 'var(--fg-tertiary)' }}
      >
        {label}
      </span>
      <span
        className={mono ? 'truncate font-mono' : 'truncate'}
        style={{ fontSize: '14px', color: 'var(--fg-primary)', textAlign: 'right' }}
      >
        {value}
      </span>
    </div>
  );
}

export type { StaffStatus, StaffRow };
